/**
 * Determines the org/admin subdomain context from the browser's location.
 *
 * In production, the browser hostname is the source of truth — e.g.
 * `acme.sitewise.app` → `{ kind: 'org', slug: 'acme' }`.
 *
 * In development, where every subdomain is `*.localhost`, browsers resolve
 * to 127.0.0.1 automatically (RFC 6761). For convenience we also accept a
 * `?subdomain=` query parameter override, mirrored into `localStorage`,
 * so devs can switch tenants without editing the URL.
 *
 * The matching API server reads the same value via the
 * `X-Sitewise-Subdomain-Override` header (set in `apiClient.ts`).
 */

const STORAGE_KEY = 'sitewise:subdomain-override';

export type Surface =
  | { kind: 'org'; slug: string }
  | { kind: 'admin' }
  | { kind: 'unknown' };

const RESERVED = new Set(['www', 'api', 'app']);

function parseHost(hostname: string): Surface {
  const parts = hostname.split('.').filter(Boolean);
  // localhost / 127.0.0.1 with no leading subdomain
  if (parts.length === 0 || parts[0] === undefined) return { kind: 'unknown' };
  if (parts.length < 2) return { kind: 'unknown' }; // bare 'localhost'

  const first = parts[0]!.toLowerCase();
  if (first === 'admin') return { kind: 'admin' };
  if (RESERVED.has(first)) return { kind: 'unknown' };
  return { kind: 'org', slug: first };
}

export function readOverride(): string | null {
  try {
    const url = new URL(window.location.href);
    const fromQuery = url.searchParams.get('subdomain');
    if (fromQuery) {
      window.localStorage.setItem(STORAGE_KEY, fromQuery);
      return fromQuery;
    }
    return window.localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

export function clearOverride(): void {
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
}

export function detectSurface(): Surface {
  const override = readOverride();
  if (override) {
    if (override === 'admin') return { kind: 'admin' };
    return { kind: 'org', slug: override };
  }
  return parseHost(window.location.hostname);
}

/** Returns the value to send in the `X-Sitewise-Subdomain-Override` header. */
export function overrideHeaderValue(surface: Surface): string | null {
  if (surface.kind === 'org') return surface.slug;
  if (surface.kind === 'admin') return 'admin';
  return null;
}
